/**
 * SOC 2 readiness — pure helpers shared by the SOC 2 page (V96-4-9), the
 * Dashboard tile and the Findings control filter. No React, no fetches.
 */
import { programHas } from '@aigovops/core';
import type { AuditPeriodRow, CertificationProfileRow, Soc2Readiness, Soc2Requirement, Soc2RequirementStatus } from './api';
import { isCouldntRequirement } from './couldnt-check';

export type Tone = 'success' | 'warning' | 'danger' | 'neutral';
export type GlyphKind = 'check' | 'x' | 'half' | 'circle' | 'warning' | 'alert' | 'pause' | 'clock' | 'dots' | 'unknown';

export const STATUS_META: Record<Soc2RequirementStatus, { label: string; glyph: GlyphKind; tone: Tone; rank: number }> = {
  gap: { label: 'Gap', glyph: 'x', tone: 'danger', rank: 0 },
  partial: { label: 'Partial', glyph: 'half', tone: 'warning', rank: 1 },
  unmapped: { label: 'Not mapped', glyph: 'circle', tone: 'neutral', rank: 2 },
  met: { label: 'Met', glyph: 'check', tone: 'success', rank: 3 },
};

export const SERIES_NAMES: Record<string, string> = {
  CC1: 'Control environment', CC2: 'Communication and information', CC3: 'Risk assessment',
  CC4: 'Monitoring activities', CC5: 'Control activities', CC6: 'Logical and physical access',
  CC7: 'System operations', CC8: 'Change management', CC9: 'Risk mitigation',
  A1: 'Availability', C1: 'Confidentiality', PI1: 'Processing integrity',
  P1: 'Privacy — notice', P2: 'Privacy — choice and consent', P3: 'Privacy — collection', P4: 'Privacy — use and retention',
  P5: 'Privacy — access', P6: 'Privacy — disclosure', P7: 'Privacy — quality', P8: 'Privacy — monitoring',
};
const SERIES_ORDER = ['CC', 'A', 'C', 'PI', 'P'];

export function seriesOf(r: Pick<Soc2Requirement, 'ref' | 'group_ref'>): string {
  if (r.group_ref) return r.group_ref;
  const m = /^([A-Z]+\d+)\./.exec(r.ref);
  return m ? m[1]! : r.ref;
}

export function seriesSort(a: string, b: string): number {
  const pa = /^([A-Z]+)(\d+)$/.exec(a), pb = /^([A-Z]+)(\d+)$/.exec(b);
  if (!pa || !pb) return a.localeCompare(b);
  const oa = SERIES_ORDER.indexOf(pa[1]!), ob = SERIES_ORDER.indexOf(pb[1]!);
  return (oa < 0 ? 99 : oa) - (ob < 0 ? 99 : ob) || Number(pa[2]) - Number(pb[2]);
}

/** CC1.1 < CC1.4 < CC6.1 < A1.2 — series order, then numeric. */
export function refSort(a: string, b: string): number {
  const pa = /^([A-Z]+)(\d+)\.(\d+)$/.exec(a), pb = /^([A-Z]+)(\d+)\.(\d+)$/.exec(b);
  if (!pa || !pb) return a.localeCompare(b);
  return seriesSort(`${pa[1]}${pa[2]}`, `${pb[1]}${pb[2]}`) || Number(pa[3]) - Number(pb[3]);
}

/** Worst first (gap → partial → not mapped → met), then by ref. */
export function worstFirst(a: Soc2Requirement, b: Soc2Requirement): number {
  return rankOf(a) - rankOf(b) || refSort(a.ref, b.ref);
}
/** V109-1-19 (B5): Couldn't check sorts right after the gaps, never among them. */
function rankOf(r: Soc2Requirement): number {
  const b = bucketOf(r);
  return b === 'couldnt' ? 0.5 : STATUS_META[b].rank;
}

const STATE_LABEL: Record<string, string> = {
  pass: 'passing', fail: 'failing', stale: 'stale', excepted: 'excepted', insufficient: 'insufficient', couldnt_check: "couldn't check", no_evidence: 'no evidence', not_applicable: 'not applicable',
};
const STATE_ORDER = ['fail', 'couldnt_check', 'stale', 'no_evidence', 'insufficient', 'excepted', 'pass', 'not_applicable'];

/** V109-1-13 (LM-9): the criterion couldn't be checked (never pass, never fail); its first reason code. */
export function couldntCheckReasonOf(r: Pick<Soc2Requirement, 'status' | 'check_state' | 'couldnt_check_reasons' | 'crediting_controls'>): string | null {
  return isCouldntRequirement(r) ? (r.couldnt_check_reasons?.[0] ?? (r.crediting_controls.some((c) => c.state === 'stale') ? 'stale' : 'collector_error')) : null;
}

/**
 * V109-1-19 (B5): the counts users see. A criterion GAC Pro couldn't check is its own segment and
 * leaves gap / partial / not mapped; "needs work" (everything not met) is the union and unchanged.
 * met + partial + gap + unmapped + couldnt = total.
 */
export interface ReadinessSplit { met: number; partial: number; gap: number; unmapped: number; couldnt: number; total: number }
export function readinessSplit(requirements: readonly Soc2Requirement[]): ReadinessSplit {
  const s: ReadinessSplit = { met: 0, partial: 0, gap: 0, unmapped: 0, couldnt: 0, total: requirements.length };
  for (const r of requirements) {
    if (r.status !== 'met' && isCouldntRequirement(r)) s.couldnt++;
    else s[r.status]++;
  }
  return s;
}

/** The criterion's bucket on the page: its status, or `couldnt` (B5: never inside gap / partial). */
export function bucketOf(r: Soc2Requirement): Soc2RequirementStatus | 'couldnt' {
  return r.status !== 'met' && isCouldntRequirement(r) ? 'couldnt' : r.status;
}

export function evidenceSummary(r: Soc2Requirement): string {
  if (r.crediting_controls.length === 0) return 'No mapped control';
  const counts = new Map<string, number>();
  for (const c of r.crediting_controls) counts.set(c.state, (counts.get(c.state) ?? 0) + 1);
  return STATE_ORDER.filter(s => counts.has(s)).map(s => `${counts.get(s)} ${STATE_LABEL[s]}`).join(' · ');
}

export type NextAction =
  | { kind: 'link'; label: string; href: string }
  | { kind: 'mailto'; label: string; href: string }
  | { kind: 'attest'; label: string; controlId: string };

const DONE_STATES = new Set(['pass', 'not_applicable', 'excepted']);

/**
 * The one next step that actually changes a non-met criterion, chosen from
 * its non-passing crediting controls:
 *   machine control failing          → Fix failing check (findings?control=)
 *   attested control not passing     → Attest (drawer → POST attestations/control)
 *   machine control stale            → Collect now   (Setup posture families)
 *   machine control without evidence → Connect source (Setup posture families)
 *   no crediting control / only partial coverage → Request coverage (mailto support)
 */
export function nextAction(r: Soc2Requirement, prefix: string, pageUrl = ''): NextAction | null {
  if (r.status === 'met') return null;
  const open = r.crediting_controls.filter(c => !DONE_STATES.has(c.state));
  const isMachine = (c: Soc2Requirement['crediting_controls'][number]) => c.evidence_mode !== 'attested';
  const failing = open.find(c => isMachine(c) && c.state === 'fail');
  if (failing) return { kind: 'link', label: 'Fix failing check', href: `${prefix}/findings?control=${encodeURIComponent(failing.control_id)}&requirement=${encodeURIComponent(r.ref)}` };
  const attest = open.find(c => c.evidence_mode === 'attested');
  if (attest) return { kind: 'attest', label: 'Attest', controlId: attest.control_id };
  // V109-1-19 (H1): a stale reading is Couldn't check · stale; one action label everywhere.
  if (open.some(c => isMachine(c) && c.state === 'stale')) return { kind: 'link', label: 'Collect again', href: `${prefix}/setup#posture-families` };
  if (open.length > 0) return { kind: 'link', label: 'Connect source', href: `${prefix}/setup#posture-families` };
  const subject = `SOC 2 coverage request: ${r.ref} ${r.title}`;
  const body = `No control in our workspace fully covers ${r.ref} (${r.title}).${pageUrl ? `\n\n${pageUrl}` : ''}`;
  return { kind: 'mailto', label: 'Request coverage', href: `mailto:support@gacpro.io?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}` };
}

const DAY_MS = 86_400_000;
function utcDay(d: string | Date): number {
  const x = typeof d === 'string' ? new Date(`${d.slice(0, 10)}T00:00:00Z`) : d;
  return Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate());
}

export interface WindowStatus { label: string; days: number; phase: 'upcoming' | 'active' | 'ended' }

export function windowStatus(period: Pick<AuditPeriodRow, 'period_start' | 'period_end'>, now = new Date()): WindowStatus {
  const today = utcDay(now), start = utcDay(period.period_start), end = utcDay(period.period_end);
  if (today < start) { const d = Math.round((start - today) / DAY_MS); return { label: `Starts in ${d} day${d === 1 ? '' : 's'}`, days: d, phase: 'upcoming' }; }
  if (today <= end) { const d = Math.round((end - today) / DAY_MS); return { label: `${d} day${d === 1 ? '' : 's'} left`, days: d, phase: 'active' }; }
  const d = Math.round((today - end) / DAY_MS);
  return { label: `Ended ${d} day${d === 1 ? '' : 's'} ago`, days: -d, phase: 'ended' };
}

export function fmtDay(d: string): string {
  const x = new Date(`${d.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(x.getTime()) ? d : x.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function addDays(day: string, n: number): string {
  return new Date(utcDay(day) + n * DAY_MS).toISOString().slice(0, 10);
}

export function addMonthsMinusDay(start: string, months: number): string {
  const d = new Date(`${start}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, d.getUTCDate()) - DAY_MS).toISOString().slice(0, 10);
}

/**
 * Active SOC 2 profile, its current window (newest planned, else the frozen
 * one) and the frozen (fieldwork) window, which stays visible beside a new
 * planned window until the report is issued and someone closes it; plus any
 * older planned windows that ended without being moved to fieldwork.
 */
export function pickSoc2Context(profiles: CertificationProfileRow[], periods: AuditPeriodRow[]) {
  // The SOC 2 page serves the program whose periods are SOC 2 report types (manifest), not a key.
  const profile = profiles.find(p => p.status === 'active' && programHas(p.program, (m) => m.period.model === 'soc2_report_type')) ?? null;
  const own = profile ? periods.filter(p => p.profile_id === profile.profile_id) : [];
  const plannedAll = own.filter(p => p.status === 'planned');
  const planned = plannedAll[0] ?? null;
  const fieldwork = own.find(p => p.status === 'fieldwork') ?? null;
  const period = planned ?? fieldwork;
  // Older planned windows that already ended (e.g. the freeze failed after the next
  // window was saved): derived from data so they can't get lost in page state.
  const unfrozen = plannedAll.slice(1).filter(p => windowStatus(p).phase === 'ended');
  return { profile, period, fieldwork, unfrozen, lastClosed: period ? null : own[0] ?? null };
}

export type VerdictKey = 'off_track' | 'at_risk' | 'on_track' | 'no_window' | 'fieldwork';
export interface Verdict { key: VerdictKey; label: string; glyph: GlyphKind; tone: Tone }

const VERDICTS: Record<VerdictKey, Verdict> = {
  off_track: { key: 'off_track', label: 'Off track', glyph: 'x', tone: 'danger' },
  at_risk: { key: 'at_risk', label: 'At risk', glyph: 'warning', tone: 'warning' },
  on_track: { key: 'on_track', label: 'On track', glyph: 'check', tone: 'success' },
  no_window: { key: 'no_window', label: 'No audit window', glyph: 'circle', tone: 'neutral' },
  fieldwork: { key: 'fieldwork', label: 'In fieldwork', glyph: 'clock', tone: 'neutral' },
};

/**
 * One verdict from readiness + window:
 *   in fieldwork — the current window is frozen and with the auditor;
 *   off track — gaps with ≤30 days left, or the window ended with gaps;
 *   at risk   — any gap or partial while the window is open or upcoming;
 *   on track  — everything else (met, or only unmapped/low items);
 *   no audit window — no current period.
 */
export function soc2Verdict(counts: Soc2Readiness['counts'], window: WindowStatus | null, periodStatus?: AuditPeriodRow['status']): Verdict {
  if (!window) return VERDICTS.no_window;
  if (periodStatus === 'fieldwork') return VERDICTS.fieldwork;
  if (counts.gap > 0 && ((window.phase === 'active' && window.days <= 30) || window.phase === 'ended')) return VERDICTS.off_track;
  if (window.phase !== 'ended' && (counts.gap > 0 || counts.partial > 0)) return VERDICTS.at_risk;
  return VERDICTS.on_track;
}
